<template>
    <div>
        <v-dialog v-model="editAppointmentModal" max-width="500px" content-class="add-appointment view-edit">
            <v-card class="event-panel">
                <div class="modal-header">
                    <h4 class="modal-headline">{{ $page.props.lang.editAppointment || "Edit Appointment" }}</h4>
                    <div class="tools">
                        <v-btn icon @click="editAppointmentModal = false"><v-icon>mdi-close</v-icon></v-btn>
                    </div>
                </div>
                <v-card-text>
                    <v-container>
                        <v-alert text color="warning" v-if="editAppointment.recurring">
                            <h4>{{$page.props.lang.recurringAppointmentTitle || "This is a recurring appointment"}}</h4>
                            <v-radio-group v-model="editAppointment.change_type">
                                <v-radio :label="$page.props.lang.changeCurrentTerm || 'Change only the current appointment'" value="once"></v-radio>
                                <v-radio :label="$page.props.lang.changeCurrentAndOtherTerms || '​Change the current and ​all recurring appointments'" value="repeat"></v-radio>
                            </v-radio-group>
                        </v-alert>
                        <div class="fields">
                            <p class="custom-label">{{ $page.props.lang.eventType || 'Class' }}</p>
                            <v-select :items="courses" item-text="title" item-value="id"
                            @change="editAppointmentCourseSelected"
                            v-model="editAppointment.course_id" :hide-details="!responseErrors.course_id" 
                            outlined dense solo flat :error-messages="responseErrors.course_id" @focus="responseErrors.course_id=''">{{ "Title" }}
                            </v-select>
                            <div class="grid">
                                <div>
                                    <p class="custom-label">{{ $page.props.lang.startDate || 'Start Date' }}</p>
                                    <v-menu
                                    v-model="editStartDatePicker"
                                    :close-on-content-click="false"
                                    :return-value.sync="today"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="290px">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-text-field 
                                            :value="editAppointment.start_date | formatDate" :label="$page.props.lang.startDate || 'Start Date'" 
                                            append-icon="mdi-menu-down" readonly v-bind="attrs" v-on="on" @click:append="editStartDatePicker = true" 
                                            outlined dense solo flat :hide-details="!responseErrors.start_date"
                                            :error-messages="responseErrors.start_date" @focus="responseErrors.start_date=''">
                                            </v-text-field>
                                        </template>
                                        <v-date-picker
                                        v-model="editAppointment.start_date" 
                                        :first-day-of-week="1" no-title scrollable color="primary">
                                            <v-spacer></v-spacer>
                                            <v-btn color="regular" @click="editStartDatePicker = false">{{ $page.props.lang.cancel || "Cancel" }}</v-btn>
                                            <v-btn color="primary" @click="editStartDatePicker = false">{{ $page.props.lang.ok || "Ok" }}</v-btn>
                                        </v-date-picker>
                                    </v-menu>
                                </div>
                                <div class="vue-timepicker-wrap">
                                    <p class="custom-label">{{ $page.props.lang.startTime || "Start time" }}</p>
                                    <vue-timepicker 
                                    v-model="editAppointment.start_time" 
                                    close-on-complete hide-disabled-items hide-clear-button fixed-dropdown-button format=""
                                    :minute-interval="15" :hour-range="[[5,23]]">
                                        <template v-slot:dropdownButton>
                                            <v-icon>mdi-menu-down</v-icon>
                                        </template>
                                    </vue-timepicker>
                                    <p v-if="responseErrors.start_time" 
                                    class="custom-errors v-bounce">
                                    {{ responseErrors.start_time[0] }}
                                    </p>
                                </div>
                            </div>
                            <p class="custom-label">{{ $page.props.lang.trainer || 'Trainer' }}</p>
                            <v-select 
                            v-model="editAppointment.trainer_id" item-value='id' item-text='full_name' 
                            :items="trainers"  
                            dense outlined :hide-details="!responseErrors.trainer_id"
                            :error-messages="responseErrors.trainer_id" @focus="responseErrors.trainer_id=''">
                            </v-select>
                            <p class="custom-label">{{ $page.props.lang.duration || 'Duration' }}</p>
                            <v-select 
                            v-model="editAppointment.duration" :items="times" 
                            dense outlined :hide-details="!responseErrors.trainer_id"
                            :error-messages="responseErrors.duration" @focus="responseErrors.duration=''">
                            </v-select>
                            <div class="grid">
                                <div>
                                    <p class="custom-label">{{ $page.props.lang.location || 'Location' }}</p>
                                    <v-select 
                                        v-model="editAppointment.location_id" :items="locationsData" 
                                        dense outlined :hide-details="!responseErrors.location_id"
                                        item-text="name" item-value="id"
                                        :error-messages="responseErrors.location_id" @focus="responseErrors.location_id=''">
                                    </v-select>
                                </div>
                                <div>
                                    <v-btn @click="openManageCategoriesModal=true" text tile small class="mt-7" color="primary">{{ $page.props.lang.manageLocations || "Manage Locations" }}</v-btn>
                                </div>
                            </div>
                            <p class="custom-label mt-3">{{ $page.props.lang.choose || "Choose appointment color" }}</p>
                            <div class="colors">
                                <template v-for="color in colors">
                                    <div @click="picked(color)" 
                                    :class="color===editAppointment.color ? 'is-selected' : ''" class="color" 
                                    :key="color" :style="{ background: color }">
                                    </div>
                                </template>
                                <p v-if="responseErrors.color" class="message-error" v-html="responseErrors.color[0]"></p>
                            </div>
                            <div v-if="editAppointment.recurring && editAppointment.change_type == 'repeat'" class="repeat-options">
                                <p class="days">
                                    Ponavljaj danima
                                    <span v-if="responseErrors.days" v-html="responseErrors.days[0]"></span>
                                </p>
                                <div class="checkboxes">
                                    <template v-for="day in weekDays">
                                        <v-checkbox v-model="editAppointment.days" :value="day.value" :key="day.value" hide-details dense class="day-check">
                                            <template v-slot:label>
                                                    <span id="checkboxLabel"> {{ day.text }}</span>
                                            </template>
                                        </v-checkbox>
                                    </template>
                                </div>
                                <p class="custom-label">Ponavljaj do</p>
                                <v-menu
                                v-model="editEndDatePicker"
                                :close-on-content-click="false"
                                :return-value.sync="today"
                                transition="scale-transition"
                                offset-y
                                min-width="290px">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field 
                                        :value="editAppointment.end_date | formatDate" append-icon="mdi-menu-down" 
                                        readonly v-bind="attrs" v-on="on" @click:append="editEndDatePicker = true" 
                                        outlined dense :hide-details="!responseErrors.end_date"
                                        :error-messages="responseErrors.end_date" @focus="responseErrors.end_date=''">
                                        </v-text-field>
                                    </template>
                                    <v-date-picker
                                    v-model="editAppointment.end_date" 
                                    :first-day-of-week="1" locale="en" no-title scrollable color="primary">
                                        <v-spacer></v-spacer>
                                        <v-btn color="regular" @click="editEndDatePicker = false">{{ $page.props.lang.cancel || "Cancel" }}</v-btn>
                                        <v-btn color="primary" @click="editEndDatePicker = false">{{ $page.props.lang.ok || "Ok" }}</v-btn>
                                    </v-date-picker>
                                </v-menu>
                            </div>

                            <v-switch v-model="editAppointment.is_online" :label="$page.props.lang.is_online || 'Online'" hide-details></v-switch>
                            <div v-if="editAppointment.is_online">
                                <p class="custom-label">{{ $page.props.lang.enter_url || 'URL' }}</p>
                                <v-text-field v-model="editAppointment.session_url" outlined dense value=""
                                :error-messages="responseErrors.session_url" @focus="responseErrors.session_url=''"
                                :hide-details="!responseErrors.session_url">
                                </v-text-field>
                            </div>
                        </div>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn color="regular" @click="editAppointmentModal = false">{{ $page.props.lang.cancel || "Cancel" }}</v-btn>
                            <v-btn color="primary" @click="edit" :loading="editEventLoading">{{ $page.props.lang.save || "Save" }}</v-btn>
                        </v-card-actions>
                    </v-container>
                </v-card-text>
            </v-card>
        </v-dialog>
        <ManageCategories 
            v-if="openManageCategoriesModal" 
            type="location" 
            :title="$page.props.lang.manageLocations || 'Manage Locations'"
            :modal="openManageCategoriesModal" 
            :categories="locations"
            @close="openManageCategoriesModal = false">
        </ManageCategories>
    </div>
</template>
<style>
    #checkboxLabel {
        margin-left: -5px;
    }
</style>
<script>
import VueTimepicker from 'vue2-timepicker'
import 'vue2-timepicker/dist/VueTimepicker.css'
import EventBus from '@/js/eventbus'
import ManageCategories from '../../../../Components/ManageCategories'


export default {
    components: {
        VueTimepicker,
        ManageCategories
    },
    props: {
        open: Boolean,
        trainers: Array,
        editAppointment: Object,
        courses: Array,
        locations: Array
    },
    data(){
        return {
            today : moment().format('YYYY-MM-DD'),
            isLoading: false,
            editStartDatePicker: false,
            editStartTimePicker: false,
            editEndDatePicker: false,
            editSearchMembers: null,
            editResultMembers: [],
            resetAppointment: {
                start_date: moment().format('YYYY-MM-DD'),
                start_time: {hh: '', mm: ''},
                end_date: moment().add(30, 'days').format('YYYY-MM-DD'),
                recurring: false,
                days: [],
                members: []
            },
            times: [
                {value: 15, text: '00:15'},
                {value: 30, text: '00:30'},
                {value: 45, text: '00:45'},
                {value: 60, text: '01:00'},
                {value: 75, text: '01:15'},
                {value: 90, text: '01:30'},
                {value: 105, text: '01:45'},
                {value: 120, text: '02:00'},
                {value: 135, text: '02:15'},
                {value: 150, text: '02:30'},
                {value: 165, text: '02:45'},
                {value: 180, text: '03:00'}
            ],
            repeating: false,
            weekDays: [
                {value:'MO', text: this.$page.props.lang.monday || 'Monday'},
                {value:'TU', text: this.$page.props.lang.tuesday || 'Tuesday'},
                {value:'WE', text: this.$page.props.lang.wednesday || 'Wednesday'},
                {value:'TH', text: this.$page.props.lang.thursday || 'Thursday'},
                {value:'FR', text: this.$page.props.lang.friday || 'Friday'},
                {value:'SA', text: this.$page.props.lang.saturday || 'Saturday'},
                {value:'SU', text: this.$page.props.lang.sunday || 'Sunday'},
            ],
            colors: [
                '#4CAF50', //green
                '#2196F3', //blue
                '#9C27B0', //purple
                '#607D8B', //blue-grey
                '#FF5722', //deep-orange
                '#CDDC39', //lime
            ],
            locationsData: this.locations,
            editEventLoading: false,
            messageObjects: [],
            responseErrors: {},
            openManageCategoriesModal: false
       }
    },
    computed: {
        editAppointmentModal: {
            get: function() {
                return this.open
            },
            set: function() {
                this.$emit('close')
                return false
            }
        },
    },
  
    watch: {
        editSearchMembers(val) {
            if (val === null) {
                this.editResultMembers = []
                return
            } 
            if(this.editResultMembers.length > 0) return
            if (this.isLoading) return

            this.isLoading = true

            axios.get('/backoffice/lookup/members', { params: { search: val }})
                .then(response => {
                    this.editResultMembers = response.data.results
                    this.isLoading = false
                })
        },
    },
    methods: {
        editAppointmentCourseSelected() {

        },
        seeUrl(){
            if(this.editAppointment.session_url.includes("/")){
                window.open(this.editAppointment.session_url);
            }else{
                 EventBus.$emit('SHOW_NOTIFICATION',{
                            message: "Not a valid URL",
                            color: 'error',
                            timeout: 10000
                        })
            }
        },
        edit() {
            this.editEventLoading = true
            let appointment = Object.assign({}, this.editAppointment)
            appointment.members = appointment.members.map(item => item.id)
            if (typeof this.editAppointment.start_time !== 'string') {
                if (this.editAppointment.start_time) {
                    appointment.start_time = this.editAppointment.start_time.HH + ':' + this.editAppointment.start_time.mm
                }
            }
            axios.post('/backoffice/calendar/appointment/update', appointment)
                .then(response => {
                     this.editEventLoading = false
                    if(response.data.status==="OK") {
                        this.$emit('fetchCurrent')
                        this.editAppointmentModal = false
                       
                        EventBus.$emit('SHOW_NOTIFICATION',{ 
                                message: response.data.message,
                                color: 'success',
                                timeout: 5000
                            })
                    }else{
                        EventBus.$emit('SHOW_NOTIFICATION',{
                                message: response.data.message,
                                color: 'error',
                                timeout: 5000
                            })
                    }
                })
                .catch(error => {
                    this.editEventLoading = false;
                    EventBus.$emit('SHOW_NOTIFICATION',{
                            message: error.response.data.message,
                            color: 'error',
                            timeout: 10000
                        })
                    
                    this.responseErrors = error.response.data.errors
                })
        },
        picked(color) {
            this.editAppointment.color = color
            this.responseErrors.color = ''
        },
        removeFromEdit(member) {
            const index = this.editAppointment.members.findIndex(item => item.id === member.id)
            
            if (index >= 0) this.editAppointment.members.splice(index, 1)
        },
    }
}
</script>